import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeStoryPackageLeadContentItemContent$key } from "~/queries/__generated__/homeStoryPackageLeadContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Headline,
  HeadlineContainer,
  LeadingImage,
  Left,
  LinkHighlightArea,
  LiveTagContainer,
  Right,
  StyledEntityLink,
  Summary,
  TailContent,
} from "./styles";

export type Props = {
  children?: ReactNode;
  reference: homeStoryPackageLeadContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemHomeStoryPackageLead = forwardRef<HTMLDivElement, Props>(
  ({ children, className, onClick, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeStoryPackageLeadContentItemContent on Content {
          ...entityLink
          entityId
        }
      `,
      reference_,
    );

    return (
      <Container className={className} ref={reference}>
        <LeadingImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size1280x720",
                mobileUp: "size540x360",
                tabletUp: "size1280x720",
              },
            })}
          </StyledEntityLink>
        </LeadingImage>
        <Left>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <LinkHighlightArea>
              <HeadlineContainer>
                <Headline>
                  {provider.headline({ hideTooltip: true, preferSocialHeadline: true })}
                </Headline>
                <LiveTagContainer>{provider.liveTag()}</LiveTagContainer>
              </HeadlineContainer>
              <Summary>{provider.subHeadline()}</Summary>
            </LinkHighlightArea>
          </StyledEntityLink>
          <ActionBar>
            {provider.actionBar({
              commentVariant: "reverse",
              isBookmarkVisible: false,
              isCommentsVisible: true,
            })}
          </ActionBar>
        </Left>
        <Right>
          <TailContent>{children}</TailContent>
        </Right>
      </Container>
    );
  },
);

ContentItemHomeStoryPackageLead.displayName = "ContentItemHomeStoryPackageLead";
